import { useEffect, useRef, useState } from "react";
import { IoLocationSharp } from "react-icons/io5";
import { searchHoliday as searchHolidayApi } from "@/services/api/holidays";
import {
  getSearchGroups,
  SEARCH_DEBOUNCE_MS,
  SEARCH_GROUP_LABELS,
  SEARCH_MIN_CHARS,
} from "@/utils/holidaySearch";
import styles from "./HolidayListing.module.css";

const GROUP_ORDER = ["cities", "countries", "states", "regions"];

function SearchGroupList({ title, items, onSelect }) {
  if (!items.length) return null;

  return (
    <div className={styles.locationPopoverGroup}>
      <p className={styles.locationPopoverGroupTitle}>{title}</p>
      <div className={styles.locationPopoverList}>
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            className={styles.locationPopoverItem}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onSelect(item)}
          >
            <span className={styles.locationPopoverPin} aria-hidden="true">
              <IoLocationSharp />
            </span>
            <span className={styles.locationPopoverItemText}>{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function LocationSearchPopover({
  anchorRef,
  query = "",
  onClose,
  onSelect,
  className = "",
  className2 = "",
}) {
  const popoverRef = useRef(null);
  const requestRef = useRef(0);
  const [groups, setGroups] = useState({
    cities: [],
    states: [],
    countries: [],
    regions: [],
  });
  const [loading, setLoading] = useState(false);

  const trimmedQuery = String(query || "").trim();
  const queryReady = trimmedQuery.length >= SEARCH_MIN_CHARS;

  useEffect(() => {
    if (!queryReady) {
      setLoading(false);
      return undefined;
    }

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);

    const timer = window.setTimeout(async () => {
      try {
        const response = await searchHolidayApi(trimmedQuery);
        if (requestRef.current !== requestId) return;
        setGroups(getSearchGroups(response));
      } catch {
        if (requestRef.current !== requestId) return;
        setGroups({ cities: [], states: [], countries: [], regions: [] });
      } finally {
        if (requestRef.current === requestId) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [trimmedQuery, queryReady]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      const anchor = anchorRef?.current;
      const popover = popoverRef.current;
      if (!popover) return;
      if (popover.contains(event.target)) return;
      if (anchor?.contains(event.target)) return;
      onClose();
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [anchorRef, onClose]);

  if (!queryReady) {
    return null;
  }
  const hasResults = GROUP_ORDER.some((key) => groups[key]?.length);

  return (
    <div
      ref={popoverRef}
      className={[styles.locationPopover, className].filter(Boolean).join(" ")}
      role="listbox"
      aria-label="Search destinations"
    >
      <div className={[styles.locationPopoverBody, className2].filter(Boolean).join(" ")}>
        {loading ? <p className={styles.locationPopoverStatus}>Loading...</p> : null}
        {!loading && !hasResults ? (
          <p className={styles.locationPopoverStatus}>No results found.</p>
        ) : null}
        {!loading && hasResults
          ? GROUP_ORDER.map((key) => (
            <SearchGroupList
              key={key}
              title={SEARCH_GROUP_LABELS[key]}
              items={groups[key]}
              onSelect={onSelect}
            />
          ))
          : null}
      </div>
    </div>
  );
}
